import { useMemo } from 'react';

import { useTranslation } from 'react-i18next';

import { StatusBadge } from '@/components/common';
import type { ColDef } from '@/grid/types';
import type { OnFarmImplantSessionEmbryo } from '@/services/on-farm-implant';

import { embryoStatusColors, statusColors } from '../FrozenTab/columnDefs';

const formatNumber = (params: { value?: number | null }) =>
  params.value == null ? '\u2014' : String(params.value);

export const useNonTogEmbryoColumnDefs = (
  readOnly: boolean,
): ColDef<OnFarmImplantSessionEmbryo>[] => {
  const { t } = useTranslation();

  return useMemo(
    () => [
      {
        colId: 'checkbox',
        headerName: '',
        width: 44,
        maxWidth: 44,
        checkboxSelection: !readOnly,
        headerCheckboxSelection: !readOnly,
        headerCheckboxSelectionFilteredOnly: true,
        suppressMovable: true,
        lockPosition: true,
        filter: false,
        sortable: false,
      },
      {
        field: 'embryoName',
        headerName: t('onFarmImplant.nonTog.embryo'),
        flex: 1.4,
        minWidth: 150,
        filter: 'agTextColumnFilter',
        cellRenderer: (params: { data?: OnFarmImplantSessionEmbryo }) => {
          if (!params.data) return null;
          return (
            <div>
              <div className="font-medium text-gray-900">
                {params.data.embryoName ?? '\u2014'}
              </div>
              <div className="text-xs text-gray-500">
                {params.data.recordName ?? ''}
              </div>
            </div>
          );
        },
      },
      {
        field: 'donorName',
        headerName: t('onFarmImplant.nonTog.donor'),
        flex: 1,
        minWidth: 120,
        filter: 'agTextColumnFilter',
      },
      {
        field: 'sireName',
        headerName: t('onFarmImplant.nonTog.sire'),
        flex: 1,
        minWidth: 120,
        filter: 'agTextColumnFilter',
      },
      {
        field: 'accountName',
        headerName: t('onFarmImplant.nonTog.owner'),
        flex: 1.2,
        minWidth: 130,
        filter: 'agTextColumnFilter',
      },
      {
        field: 'stage',
        headerName: t('onFarmImplant.nonTog.stage'),
        flex: 0.5,
        minWidth: 70,
        filter: 'agNumberColumnFilter',
        valueFormatter: formatNumber,
      },
      {
        field: 'grade',
        headerName: t('onFarmImplant.nonTog.grade'),
        flex: 0.5,
        minWidth: 70,
        filter: 'agNumberColumnFilter',
        valueFormatter: formatNumber,
      },
      {
        field: 'statusName',
        headerName: t('onFarmImplant.nonTog.workflowStatus'),
        flex: 0.8,
        minWidth: 110,
        filter: 'agTextColumnFilter',
        cellRenderer: (params: { value?: string }) => {
          if (!params.value) return null;
          return <StatusBadge label={params.value} colorMap={statusColors} />;
        },
      },
      {
        field: 'embryoStatusName',
        headerName: t('onFarmImplant.frozen.embryoStatus'),
        flex: 0.9,
        minWidth: 120,
        filter: 'agTextColumnFilter',
        cellRenderer: (params: { value?: string }) => {
          if (!params.value) return null;
          return (
            <StatusBadge label={params.value} colorMap={embryoStatusColors} />
          );
        },
      },
    ],
    [readOnly, t],
  );
};
